import React, { useEffect, useState } from 'react';
import { Button, Modal, Space, Switch, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { analyticsAllowed, getConsent, hasDecided, setConsent } from '../lib/cookieConsent';
import { initAnalyticsIfConsented } from '../firebase';

const { Text, Paragraph } = Typography;

/**
 * GDPR-aware cookie banner; gates Firebase Analytics until consent.
 */
export const CookieConsentBanner: React.FC = () => {
  const [visible, setVisible] = useState(() => typeof window !== 'undefined' && !hasDecided());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);

  useEffect(() => {
    if (hasDecided()) {
      if (analyticsAllowed()) initAnalyticsIfConsented();
      return;
    }
    setVisible(true);
  }, []);

  const saveChoice = (choice: 'essential_only' | 'all') => {
    setConsent(choice);
    if (choice === 'all') initAnalyticsIfConsented();
    setVisible(false);
    setSettingsOpen(false);
  };

  const openSettings = () => {
    const current = getConsent();
    setAnalyticsOn(current?.choice === 'all');
    setSettingsOpen(true);
  };

  if (!visible && !settingsOpen) {
    return (
      <button
        type="button"
        className="cookie-settings-fab"
        onClick={openSettings}
        aria-label="Cookie settings"
      >
        Cookies
      </button>
    );
  }

  return (
    <>
      {visible && (
        <div className="cookie-consent-banner" role="dialog" aria-label="Cookie consent">
          <div className="cookie-consent-inner">
            <Paragraph style={{ marginBottom: 12, color: 'var(--text-primary)' }}>
              We use essential cookies for sign-in and optional analytics (Firebase/Google) to improve
              TrackiAI. Under Singapore PDPA and for visitors in the EU/UK, you may accept or decline
              analytics cookies. See our{' '}
              <Link to="/privacy">Privacy Policy</Link> and{' '}
              <Link to="/privacy#cookies">Cookies</Link>.
            </Paragraph>
            <Space wrap>
              <Button type="primary" onClick={() => saveChoice('all')}>
                Accept all
              </Button>
              <Button onClick={() => saveChoice('essential_only')}>Essential only</Button>
              <Button type="link" onClick={openSettings}>
                Cookie settings
              </Button>
            </Space>
          </div>
        </div>
      )}
      <Modal
        title="Cookie settings"
        open={settingsOpen}
        onCancel={() => setSettingsOpen(false)}
        footer={[
          <Button key="essential" onClick={() => saveChoice('essential_only')}>
            Essential only
          </Button>,
          <Button key="save" type="primary" onClick={() => saveChoice(analyticsOn ? 'all' : 'essential_only')}>
            Save preferences
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Essential</Text>
          <br />
          <Text type="secondary">Required for authentication. Always on.</Text>
          <br />
          <Switch checked disabled style={{ marginTop: 8 }} aria-label="Essential cookies enabled" />
        </div>
        <div>
          <Text strong>Analytics</Text>
          <br />
          <Text type="secondary">Firebase Analytics — helps us understand site usage.</Text>
          <br />
          <Switch
            checked={analyticsOn}
            onChange={setAnalyticsOn}
            style={{ marginTop: 8 }}
            aria-label="Analytics cookies"
          />
        </div>
      </Modal>
    </>
  );
};

export default CookieConsentBanner;
